import React, { useState, useEffect, useCallback } from 'react';
import { 
  Settings, 
  Save, 
  RefreshCw, 
  MessageCircle, 
  Phone, 
  Users, 
  Building, 
  CreditCard, 
  Bell, 
  Shield, 
  Database, 
  Wifi,
  WifiOff,
  CheckCircle,
  AlertCircle,
  Eye,
  EyeOff,
  Play,
  Smartphone,
  Send,
  Key,
  Globe,
  Server,
  Clock,
  DollarSign,
  Percent,
  FileText,
  Image,
  Palette,
  Download,
  Upload,
  QrCode,
  Copy
} from 'lucide-react';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { useAuth } from '../../contexts/AuthContext';
import { dataHelpers } from '../../lib/supabase';
import toast from 'react-hot-toast';
import CepAutoFill from '../../components/forms/CepAutoFill';

const SettingsPage = () => {
  const { company, person } = useAuth();
  const [activeTab, setActiveTab] = useState('general');
  const [loading, setLoading] = useState(false);
  const [testingWhatsApp, setTestingWhatsApp] = useState(false);  const [testingAsaas, setTestingAsaas] = useState(false);
  const [testingWebhook, setTestingWebhook] = useState(false);
  const [configuringWebhook, setConfiguringWebhook] = useState(false);
  const [testingWebhookCallback, setTestingWebhookCallback] = useState(false);
  const [creatingDefaultCustomer, setCreatingDefaultCustomer] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [showAsaasToken, setShowAsaasToken] = useState(false);
  const [webhookStatus, setWebhookStatus] = useState(null);
  const [webhookLogs, setWebhookLogs] = useState([]);
  
  // Estados para configurações gerais
  const [generalSettings, setGeneralSettings] = useState({
    companyName: company?.name || '',
    cnpj: company?.document || '',
    email: company?.email || '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    timezone: 'America/Sao_Paulo',
    currency: 'BRL',
    taxRate: 0,
    language: 'pt-BR'
  });

  // Estados para configurações do WhatsApp
  const [whatsappSettings, setWhatsappSettings] = useState({
    enabled: false,
    apiUrl: '',
    apiKey: '',
    instanceName: '',
    webhookUrl: '',
    autoResponse: true,
    welcomeMessage: 'Olá! Bem-vindo à nossa cantina. Como posso ajudá-lo?',
    balanceMessage: 'Seu saldo atual é: R$ {balance}',
    lowBalanceAlert: true,
    lowBalanceThreshold: 10.00,
    paymentConfirmation: true,
    receiptMessage: 'Recibo da sua compra:\n{receipt}',
    businessHours: {
      enabled: true,
      start: '07:00',
      end: '17:00',
      message: 'Nosso atendimento funciona das 7h às 17h. Retornaremos em breve!'
    }
  });
  // Estados para configurações de notificações
  const [notificationSettings, setNotificationSettings] = useState({
    emailNotifications: true,
    whatsappNotifications: false,
    lowStockAlert: true,
    lowBalanceAlert: true
  });
  // Estados para configurações PIX
  const [pixSettings, setPixSettings] = useState({
    token: '',
    api_url: 'https://sandbox.asaas.com',
    environment: 'sandbox',
    active: false,
    webhook_auth_token: '',
    webhook_email: person?.email || '',
    default_customer_id: null
  });
  const tabs = [
    { id: 'general', name: 'Geral', icon: Building },
    { id: 'pix', name: 'API Asaas', icon: QrCode },
    { id: 'whatsapp', name: 'WhatsApp', icon: MessageCircle },
    { id: 'notifications', name: 'Notificações', icon: Bell }
  ];

  useEffect(() => {
    loadSettings();
  }, []);
  const loadSettings = async () => {
    try {
      setLoading(true);
      
      // Carregar configurações gerais, WhatsApp e notificações
      const settings = await dataHelpers.getCompanySettings(company.id);
      
      if (settings) {
        setGeneralSettings(prev => ({
          ...prev,
          ...settings.general,
          companyName: settings.general.companyName || company?.name || '',
          cnpj: settings.general.cnpj || company?.document || '',
          email: settings.general.email || company?.email || ''
        }));
        setWhatsappSettings(prev => ({ ...prev, ...settings.whatsapp }));
        setNotificationSettings(prev => ({ ...prev, ...settings.notifications }));
      }
        // Carregar configurações PIX
      const pixConfig = await dataHelpers.getPixSettings(company.id);
      if (pixConfig) {
        setPixSettings({
          token: pixConfig.token || '',
          api_url: pixConfig.api_url || 'https://sandbox.asaas.com',
          environment: pixConfig.environment || 'sandbox',
          active: pixConfig.active || false,
          webhook_auth_token: pixConfig.webhook_auth_token || '',
          webhook_email: pixConfig.webhook_email || person?.email || '',
          default_customer_id: pixConfig.default_customer_id || null
        });
      }

      // Carregar logs do webhook se estiver na aba PIX
      if (activeTab === 'pix') {
        await loadWebhookLogs();
      }
      
      toast.success('Configurações carregadas');
    } catch (error) {
      console.error('Error loading settings:', error);
      toast.error('Erro ao carregar configurações');
    } finally {
      setLoading(false);
    }
  };
  const saveSettings = async () => {
    try {
      setLoading(true);
        const allSettings = {
        general: generalSettings,
        whatsapp: whatsappSettings,
        notifications: notificationSettings
      };

      await dataHelpers.updateCompanySettings(company.id, allSettings);
      
      toast.success('Configurações salvas com sucesso!');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Erro ao salvar configurações');
    } finally {
      setLoading(false);
    }
  };
  const testWhatsAppConnection = async () => {
    try {
      setTestingWhatsApp(true);
      
      // Verificar se todos os campos necessários estão preenchidos
      if (!whatsappSettings.apiUrl || !whatsappSettings.apiKey || !whatsappSettings.instanceName) {
        toast.error('Preencha URL da API, API Key e Nome da Instância para testar a conexão');
        return;
      }
      
      const result = await dataHelpers.testWhatsAppConnection(
        whatsappSettings.apiUrl,
        whatsappSettings.apiKey,
        whatsappSettings.instanceName
      );
      
      if (result.success && result.connected) {
        toast.success(result.message);
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      console.error('WhatsApp test error:', error);
      toast.error('Erro ao testar conexão WhatsApp');
    } finally {
      setTestingWhatsApp(false);
    }
  };

  const savePixSettings = async () => {
    try {
      setLoading(true);
      
      await dataHelpers.savePixSettings(company.id, pixSettings);
      
      toast.success('Configurações PIX salvas com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar configurações PIX:', error);
      toast.error('Erro ao salvar configurações PIX');
    } finally {
      setLoading(false);
    }
  };

  const testAsaasConnection = async () => {
    try {
      setTestingAsaas(true);
      
      // Verificar se as configurações básicas estão preenchidas
      if (!pixSettings.api_url || !pixSettings.token) {
        toast.error('Preencha URL da API e Token para testar a conexão');
        return;
      }
      
      // Salvar configurações antes de testar
      await savePixSettings();
      
      const result = await dataHelpers.testAsaasConnection(company.id);
      
      if (result.success) {
        const message = `${result.message}\nAmbiente: ${result.environment}\nURL: ${result.api_url}`;
        toast.success(message);
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      console.error('Asaas test error:', error);
      toast.error('Erro ao testar conexão Asaas');
    } finally {
      setTestingAsaas(false);
    }
  };

  const configureWebhook = async () => {
    try {
      setConfiguringWebhook(true);
      
      // Verificar se email está preenchido
      if (!pixSettings.webhook_email) {
        toast.error('Preencha Email para configurar webhook');
        return;
      }
      
      // Usar nossa URL do Supabase como webhook URL
      const webhookUrl = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/asaas-webhook-callback`;
      
      const result = await dataHelpers.configureAsaasWebhook(
        company.id,
        webhookUrl,
        pixSettings.webhook_email
      );
      
      if (result.success) {
        toast.success('Webhook configurado com sucesso!');
        // Atualizar estado local com o authToken
        setPixSettings(prev => ({ 
          ...prev, 
          webhook_auth_token: result.authToken 
        }));
        // Carregar status do webhook
        await loadWebhookStatus();
      } else {
        toast.error(result.message || 'Erro ao configurar webhook');
      }
    } catch (error) {
      console.error('Webhook configuration error:', error);
      toast.error('Erro ao configurar webhook');
    } finally {
      setConfiguringWebhook(false);
    }
  };

  const loadWebhookStatus = async () => {
    try {
      setTestingWebhook(true);
      
      const result = await dataHelpers.getAsaasWebhookStatus(company.id);
      
      if (result.success) {
        setWebhookStatus(result.webhook);
      } else {
        setWebhookStatus(null);
      }
    } catch (error) {
      console.error('Webhook status error:', error);
      setWebhookStatus(null);
    } finally {
      setTestingWebhook(false);
    }
  };

  const loadWebhookLogs = async () => {
    try {
      const logs = await dataHelpers.getWebhookLogs(10);
      setWebhookLogs(logs);
    } catch (error) {
      console.error('Erro ao carregar logs do webhook:', error);
      toast.error('Erro ao carregar logs do webhook');
    }
  };

  const testWebhookCallback = async () => {
    try {
      setTestingWebhookCallback(true);
      
      // Pegar primeiro estudante para teste (você pode melhorar isso)
      const studentId = prompt('Digite o ID do estudante para teste:');
      const amount = prompt('Digite o valor para teste (ex: 10.50):');
      
      if (!studentId || !amount) {
        toast.error('ID do estudante e valor são obrigatórios');
        return;
      }
      
      const result = await dataHelpers.testWebhookCallback(studentId, parseFloat(amount));
      
      if (result.success) {
        toast.success('Webhook de teste enviado com sucesso!');
        // Recarregar logs após o teste
        await loadWebhookLogs();
      } else {
        toast.error('Erro ao enviar webhook de teste');
      }
    } catch (error) {
      console.error('Erro no teste de webhook:', error);
      toast.error('Erro ao testar webhook');
    } finally {
      setTestingWebhookCallback(false);
    }
  };

  const copyWebhookUrl = () => {
    const webhookUrl = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/asaas-webhook-callback`;
    navigator.clipboard.writeText(webhookUrl);
    toast.success('URL do webhook copiada!');
  };

  const sendTestMessage = async () => {
    try {
      const testNumber = prompt('Digite o número para teste (com DDI, ex: 5511999999999):');
      if (!testNumber) return;

      // Verificar se todos os campos necessários estão preenchidos
      if (!whatsappSettings.apiUrl || !whatsappSettings.apiKey || !whatsappSettings.instanceName) {
        toast.error('Preencha URL da API, API Key e Nome da Instância para enviar mensagem');
        return;
      }

      const result = await dataHelpers.sendWhatsAppMessage(
        whatsappSettings.apiUrl,
        whatsappSettings.apiKey,
        whatsappSettings.instanceName,
        testNumber,
        'Teste de integração CantinaGo - Sistema funcionando corretamente! 🎉'
      );

      if (result.success) {
        toast.success(result.message);
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      console.error('Send test message error:', error);
      toast.error('Erro ao enviar mensagem de teste');
    }
  };
  const handleCepAddress = useCallback((cepData) => {
    setGeneralSettings(prev => ({
      ...prev,
      address: cepData.street || prev.address,
      city: cepData.city || prev.city,
      state: cepData.state || prev.state,
      zipCode: cepData.cep || prev.zipCode
    }));
  }, []);

  const renderGeneralSettings = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Informações da Empresa</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Nome da Empresa"
            value={generalSettings.companyName}
            onChange={(e) => setGeneralSettings({...generalSettings, companyName: e.target.value})}
            icon={Building}
          />
          <Input
            label="CNPJ"
            value={generalSettings.cnpj}
            onChange={(e) => setGeneralSettings({...generalSettings, cnpj: e.target.value})}
            placeholder="00.000.000/0000-00"
          />
          <Input
            label="E-mail"
            type="email"
            value={generalSettings.email}
            onChange={(e) => setGeneralSettings({...generalSettings, email: e.target.value})}
          />
          <Input
            label="Telefone"
            value={generalSettings.phone}
            onChange={(e) => setGeneralSettings({...generalSettings, phone: e.target.value})}
            icon={Phone}
            placeholder="(11) 99999-9999"
          />
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Endereço</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <Input
              label="Endereço"
              value={generalSettings.address}
              onChange={e => setGeneralSettings({...generalSettings, address: e.target.value})}
              placeholder="Rua, número, complemento"
            />
          </div>
          <Input
            label="Cidade"
            value={generalSettings.city}
            onChange={e => setGeneralSettings({...generalSettings, city: e.target.value})}
          />
          <Input
            label="Estado"
            value={generalSettings.state}
            onChange={e => setGeneralSettings({...generalSettings, state: e.target.value})}
            placeholder="SP"
          />
          <CepAutoFill
            label="CEP"
            value={generalSettings.zipCode}
            onAddress={handleCepAddress}
            onChange={e => setGeneralSettings({...generalSettings, zipCode: e.target.value})}
            placeholder="00000-000"
            name="zipCode"
          />
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Configurações Regionais</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Fuso Horário</label>
            <select 
              value={generalSettings.timezone}
              onChange={(e) => setGeneralSettings({...generalSettings, timezone: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="America/Sao_Paulo">Brasil (GMT-3)</option>
              <option value="America/New_York">Nova York (GMT-5)</option>
              <option value="Europe/London">Londres (GMT+0)</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Moeda</label>
            <select 
              value={generalSettings.currency}
              onChange={(e) => setGeneralSettings({...generalSettings, currency: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="BRL">Real (R$)</option>
              <option value="USD">Dólar ($)</option>
              <option value="EUR">Euro (€)</option>
            </select>
          </div>
          <Input
            label="Taxa de Imposto (%)"
            type="number"
            value={generalSettings.taxRate}
            onChange={(e) => setGeneralSettings({...generalSettings, taxRate: parseFloat(e.target.value)})}
            icon={Percent}
            placeholder="0.00"
          />
        </div>
      </div>
    </div>
  );

  const renderPixSettings = () => (
    <div className="space-y-6">      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Configurações API Asaas</h3>
          <p className="text-sm text-gray-600 mt-1">Configure a integração com a API Asaas para pagamentos PIX</p>
        </div>
        <div className="flex items-center">
          <input
            type="checkbox"
            id="pixActive"
            checked={pixSettings.active}
            onChange={(e) => setPixSettings(prev => ({ ...prev, active: e.target.checked }))}
            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
          />
          <label htmlFor="pixActive" className="ml-2 text-sm font-medium text-gray-700">
            PIX Ativo
          </label>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Ambiente
          </label>
          <select
            value={pixSettings.environment}
            onChange={(e) => {
              const environment = e.target.value;
              setPixSettings(prev => ({ 
                ...prev, 
                environment,
                api_url: environment === 'sandbox' ? 'https://sandbox.asaas.com' : 'https://www.asaas.com'
              }));
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="sandbox">Sandbox (Teste)</option>
            <option value="production">Produção</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            URL da API
          </label>
          <Input
            type="url"
            value={pixSettings.api_url}
            onChange={(e) => setPixSettings(prev => ({ ...prev, api_url: e.target.value }))}
            placeholder="https://sandbox.asaas.com"
            className="w-full"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Token de Acesso Asaas
        </label>
        <div className="relative">
          <Input
            type={showAsaasToken ? "text" : "password"}
            value={pixSettings.token}
            onChange={(e) => setPixSettings(prev => ({ ...prev, token: e.target.value }))}
            placeholder="$aact_..."
            className="w-full pr-10"
          />
          <button
            type="button"
            onClick={() => setShowAsaasToken(!showAsaasToken)}
            className="absolute inset-y-0 right-0 pr-3 flex items-center"
          >
            {showAsaasToken ? (
              <EyeOff className="w-4 h-4 text-gray-400" />
            ) : (
              <Eye className="w-4 h-4 text-gray-400" />
            )}
          </button>
        </div>
        <p className="text-xs text-gray-500 mt-1">
          Token de acesso para autenticação na API Asaas
        </p>
      </div>

      {/* Configurações do Webhook */}
      <div className="border-t border-gray-200 pt-6">
        <h4 className="text-md font-medium text-gray-900 mb-4">Configurações de Webhook</h4>
        
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Email para Notificações
          </label>
          <Input
            type="email"
            value={pixSettings.webhook_email}
            onChange={(e) => setPixSettings(prev => ({ ...prev, webhook_email: e.target.value }))}
            placeholder="admin@empresa.com"
            className="w-full"
          />
          <p className="text-xs text-gray-500 mt-1">
            Email para receber notificações de webhook
          </p>
        </div>

        {/* Status do Webhook */}
        {webhookStatus && (
          <div className="bg-gray-50 p-4 rounded-lg mb-4">
            <div className="flex items-center justify-between">
              <div>
                <h5 className="text-sm font-medium text-gray-900">Status do Webhook</h5>
                <p className="text-xs text-gray-600 mt-1">
                  URL: {webhookStatus.url}
                </p>
                <p className="text-xs text-gray-600">
                  Email: {webhookStatus.email}
                </p>
              </div>
              <div className="flex items-center">
                {webhookStatus.enabled ? (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Ativo
                  </span>
                ) : (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                    <AlertCircle className="w-3 h-3 mr-1" />
                    Inativo
                  </span>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Auth Token do Webhook */}
        {pixSettings.webhook_auth_token && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Token de Autenticação do Webhook
            </label>
            <div className="bg-gray-50 p-3 rounded border text-sm font-mono break-all">
              {pixSettings.webhook_auth_token}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Use este token para validar requisições do webhook
            </p>
          </div>
        )}

        {/* URL do Webhook Callback */}
        <div className="border-t border-gray-200 pt-4">
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-gray-700">
              URL do Webhook Callback
            </label>
            <Button
              onClick={copyWebhookUrl}
              variant="outline"
              size="sm"
              className="flex items-center"
            >
              <Copy className="w-3 h-3 mr-1" />
              Copiar
            </Button>
          </div>
          <div className="bg-gray-50 p-3 rounded border text-sm font-mono break-all">
            {process.env.REACT_APP_SUPABASE_URL}/functions/v1/asaas-webhook-callback
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Configure esta URL no Asaas para receber notificações de pagamento
          </p>
        </div>

        {/* Logs do Webhook */}
        {webhookLogs.length > 0 && (
          <div className="border-t border-gray-200 pt-4">
            <div className="flex items-center justify-between mb-3">
              <h5 className="text-sm font-medium text-gray-900">Últimos Webhooks</h5>
              <Button
                onClick={loadWebhookLogs}
                variant="outline"
                size="sm"
                className="flex items-center"
              >
                <RefreshCw className="w-3 h-3 mr-1" />
                Atualizar
              </Button>
            </div>
            <div className="max-h-40 overflow-y-auto">
              {webhookLogs.map((log) => (
                <div key={log.id} className="flex items-center justify-between p-2 border-b border-gray-100 last:border-b-0">
                  <div>
                    <span className="text-xs font-medium text-gray-900">{log.event_type}</span>
                    {log.external_reference && (
                      <span className="text-xs text-gray-500 ml-2">{log.external_reference}</span>
                    )}
                  </div>
                  <div className="text-right">
                    {log.payment_value && (
                      <div className="text-xs font-medium text-green-600">
                        R$ {parseFloat(log.payment_value).toFixed(2)}
                      </div>
                    )}
                    <div className="text-xs text-gray-400">
                      {new Date(log.received_at).toLocaleString('pt-BR')}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Configurações do Cliente Padrão */}
      <div className="border-t border-gray-200 pt-6">
        <h4 className="text-md font-medium text-gray-900 mb-4">Cliente "Consumidor Final"</h4>
        <p className="text-sm text-gray-600 mb-4">
          Configure um cliente padrão para vendas PDV sem aluno específico. 
          Este cliente será usado automaticamente em vendas anônimas.
        </p>
        
        {pixSettings.default_customer_id ? (
          <div className="bg-green-50 p-4 rounded-lg mb-4">
            <div className="flex items-center justify-between">
              <div>
                <h5 className="text-sm font-medium text-green-900">Cliente Configurado</h5>
                <p className="text-xs text-green-700 mt-1">
                  ID: {pixSettings.default_customer_id}
                </p>
                <p className="text-xs text-green-700">
                  Este cliente será usado em vendas PDV anônimas
                </p>
              </div>
              <div className="flex items-center">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  <CheckCircle className="w-3 h-3 mr-1" />
                  Ativo
                </span>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-yellow-50 p-4 rounded-lg mb-4">
            <div className="flex items-center">
              <AlertCircle className="w-5 h-5 text-yellow-600 mr-2" />
              <div>
                <p className="text-sm font-medium text-yellow-800">
                  Cliente "Consumidor Final" não configurado
                </p>
                <p className="text-xs text-yellow-700 mt-1">
                  Crie um cliente padrão para vendas PDV anônimas. O cliente será criado com os dados da empresa.
                </p>
              </div>
            </div>
          </div>
        )}
        
        <Button
          onClick={createDefaultCustomer}
          disabled={creatingDefaultCustomer || !pixSettings.token}
          className="flex items-center"
          variant={pixSettings.default_customer_id ? "outline" : "primary"}
        >
          {creatingDefaultCustomer ? (
            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Users className="w-4 h-4 mr-2" />
          )}
          {pixSettings.default_customer_id ? 'Recriar Cliente' : 'Criar Cliente'}
        </Button>
      </div>

      {/* Informações de ambiente */}
      <div className={`p-4 rounded-lg ${pixSettings.environment === 'sandbox' ? 'bg-yellow-50' : 'bg-green-50'}`}>
        <div className="flex items-center">
          {pixSettings.environment === 'sandbox' ? (
            <AlertCircle className="w-5 h-5 text-yellow-600 mr-2" />
          ) : (
            <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
          )}
          <div>
            <p className={`text-sm font-medium ${pixSettings.environment === 'sandbox' ? 'text-yellow-800' : 'text-green-800'}`}>
              {pixSettings.environment === 'sandbox' ? 'Ambiente de Teste' : 'Ambiente de Produção'}
            </p>
            <p className={`text-xs ${pixSettings.environment === 'sandbox' ? 'text-yellow-700' : 'text-green-700'}`}>
              {pixSettings.environment === 'sandbox' 
                ? 'Use este ambiente para testes. Nenhuma cobrança real será feita.'
                : 'Ambiente real. Cobranças serão processadas efetivamente.'
              }
            </p>
          </div>
        </div>
      </div>

      {/* Botões de teste e ação */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-4">
        <div className="flex gap-2">
          <Button
            onClick={testAsaasConnection}
            disabled={testingAsaas || !pixSettings.api_url || !pixSettings.token}
            className="flex items-center flex-1"
            variant="outline"
          >
            {testingAsaas ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Wifi className="w-4 h-4 mr-2" />}
            Testar API
          </Button>
          <Button
            onClick={savePixSettings}
            disabled={loading}
            className="flex items-center flex-1"
          >
            {loading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Salvar
          </Button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          <Button
            onClick={configureWebhook}
            disabled={configuringWebhook || !pixSettings.webhook_email || !pixSettings.token}
            className="flex items-center"
            variant="outline"
          >
            {configuringWebhook ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Settings className="w-4 h-4 mr-2" />}
            Configurar
          </Button>
          <Button
            onClick={loadWebhookStatus}
            disabled={testingWebhook || !pixSettings.token}
            className="flex items-center"
            variant="outline"
          >
            {testingWebhook ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
            Status
          </Button>
          <Button
            onClick={testWebhookCallback}
            disabled={testingWebhookCallback}
            className="flex items-center"
            variant="outline"
          >
            {testingWebhookCallback ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
            Testar
          </Button>
        </div>
      </div>
    </div>
  );

  const renderWhatsAppSettings = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Integração WhatsApp</h3>
          <p className="text-sm text-gray-600">Configure a integração com Evolution API</p>
        </div>
        <div className="flex items-center">
          <span className="mr-3 text-sm text-gray-700">Ativar WhatsApp</span>
          <button
            onClick={() => setWhatsappSettings({...whatsappSettings, enabled: !whatsappSettings.enabled})}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              whatsappSettings.enabled ? 'bg-green-600' : 'bg-gray-200'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                whatsappSettings.enabled ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
      </div>

      {whatsappSettings.enabled && (
        <>
          <div>
            <h4 className="text-md font-medium text-gray-900 mb-3">Configurações da API</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="URL da API"
                value={whatsappSettings.apiUrl}
                onChange={(e) => setWhatsappSettings({...whatsappSettings, apiUrl: e.target.value})}
                icon={Globe}
                placeholder="https://api.evolutionapi.com"
              />
              <div className="relative">
                <Input
                  label="API Key"
                  type={showApiKey ? 'text' : 'password'}
                  value={whatsappSettings.apiKey}
                  onChange={(e) => setWhatsappSettings({...whatsappSettings, apiKey: e.target.value})}
                  icon={Key}
                  placeholder="sua-api-key-aqui"
                />
                <button
                  type="button"
                  onClick={() => setShowApiKey(!showApiKey)}
                  className="absolute right-3 top-8 text-gray-400 hover:text-gray-600"
                >
                  {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <Input
                label="Nome da Instância"
                value={whatsappSettings.instanceName}
                onChange={(e) => setWhatsappSettings({...whatsappSettings, instanceName: e.target.value})}
                icon={Smartphone}
                placeholder="cantina-instance"
              />
              <Input
                label="Webhook URL"
                value={whatsappSettings.webhookUrl}
                onChange={(e) => setWhatsappSettings({...whatsappSettings, webhookUrl: e.target.value})}
                icon={Server}
                placeholder="https://seu-sistema.com/webhook"
              />
            </div>
            
            <div className="flex gap-3 mt-4">
              <Button
                onClick={testWhatsAppConnection}
                disabled={testingWhatsApp || !whatsappSettings.apiUrl || !whatsappSettings.apiKey}
                className="flex items-center"
                variant="outline"
              >
                {testingWhatsApp ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Wifi className="w-4 h-4 mr-2" />}
                Testar Conexão
              </Button>
              <Button
                onClick={sendTestMessage}
                disabled={testingWhatsApp || !whatsappSettings.apiUrl || !whatsappSettings.apiKey}
                className="flex items-center"
                variant="outline"
              >
                <Send className="w-4 h-4 mr-2" />
                Enviar Teste
              </Button>
            </div>
          </div>

          <div>
            <h4 className="text-md font-medium text-gray-900 mb-3">Mensagens Automáticas</h4>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Mensagem de Boas-vindas</label>
                <textarea
                  value={whatsappSettings.welcomeMessage}
                  onChange={(e) => setWhatsappSettings({...whatsappSettings, welcomeMessage: e.target.value})}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Mensagem enviada quando cliente inicia conversa"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Mensagem de Saldo</label>
                <textarea
                  value={whatsappSettings.balanceMessage}
                  onChange={(e) => setWhatsappSettings({...whatsappSettings, balanceMessage: e.target.value})}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Use {balance} para mostrar o saldo"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Mensagem de Recibo</label>
                <textarea
                  value={whatsappSettings.receiptMessage}
                  onChange={(e) => setWhatsappSettings({...whatsappSettings, receiptMessage: e.target.value})}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Use {receipt} para mostrar detalhes da compra"
                />
              </div>
            </div>
          </div>

          <div>
            <h4 className="text-md font-medium text-gray-900 mb-3">Alertas e Notificações</h4>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-sm font-medium text-gray-700">Alerta de Saldo Baixo</span>
                  <p className="text-xs text-gray-500">Notificar quando saldo estiver baixo</p>
                </div>
                <button
                  onClick={() => setWhatsappSettings({...whatsappSettings, lowBalanceAlert: !whatsappSettings.lowBalanceAlert})}
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                    whatsappSettings.lowBalanceAlert ? 'bg-green-600' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                      whatsappSettings.lowBalanceAlert ? 'translate-x-5' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              {whatsappSettings.lowBalanceAlert && (
                <div className="ml-4">
                  <Input
                    label="Valor mínimo para alerta (R$)"
                    type="number"
                    value={whatsappSettings.lowBalanceThreshold}
                    onChange={(e) => setWhatsappSettings({...whatsappSettings, lowBalanceThreshold: parseFloat(e.target.value)})}
                    placeholder="10.00"
                  />
                </div>
              )}

              <div className="flex items-center justify-between">
                <div>
                  <span className="text-sm font-medium text-gray-700">Confirmação de Pagamento</span>
                  <p className="text-xs text-gray-500">Enviar recibo automático após compra</p>
                </div>
                <button
                  onClick={() => setWhatsappSettings({...whatsappSettings, paymentConfirmation: !whatsappSettings.paymentConfirmation})}
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                    whatsappSettings.paymentConfirmation ? 'bg-green-600' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                      whatsappSettings.paymentConfirmation ? 'translate-x-5' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>
          </div>

          <div>
            <h4 className="text-md font-medium text-gray-900 mb-3">Horário de Funcionamento</h4>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">Ativar Horário Comercial</span>
                <button
                  onClick={() => setWhatsappSettings({
                    ...whatsappSettings, 
                    businessHours: {...whatsappSettings.businessHours, enabled: !whatsappSettings.businessHours.enabled}
                  })}
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                    whatsappSettings.businessHours.enabled ? 'bg-green-600' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                      whatsappSettings.businessHours.enabled ? 'translate-x-5' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              {whatsappSettings.businessHours.enabled && (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Abertura</label>
                      <input
                        type="time"
                        value={whatsappSettings.businessHours.start}
                        onChange={(e) => setWhatsappSettings({
                          ...whatsappSettings,
                          businessHours: {...whatsappSettings.businessHours, start: e.target.value}
                        })}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Fechamento</label>
                      <input
                        type="time"
                        value={whatsappSettings.businessHours.end}
                        onChange={(e) => setWhatsappSettings({
                          ...whatsappSettings,
                          businessHours: {...whatsappSettings.businessHours, end: e.target.value}
                        })}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Mensagem Fora do Horário</label>
                    <textarea
                      value={whatsappSettings.businessHours.message}
                      onChange={(e) => setWhatsappSettings({
                        ...whatsappSettings,
                        businessHours: {...whatsappSettings.businessHours, message: e.target.value}
                      })}
                      rows={2}
                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
  const renderNotificationSettings = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Preferências de Notificação</h3>
        <div className="space-y-4">
          {[
            { key: 'emailNotifications', label: 'Notificações por E-mail', description: 'Receber alertas por e-mail' },
            { key: 'whatsappNotifications', label: 'Notificações por WhatsApp', description: 'Receber alertas via WhatsApp' },
            { key: 'lowStockAlert', label: 'Alerta de Estoque Baixo', description: 'Notificar quando produtos estiverem com estoque baixo' },
            { key: 'lowBalanceAlert', label: 'Alerta de Saldo Baixo', description: 'Notificar quando alunos estiverem com saldo baixo' }
          ].map((setting) => (
            <div key={setting.key} className="flex items-center justify-between py-3 border-b border-gray-100">
              <div>
                <span className="text-sm font-medium text-gray-700">{setting.label}</span>
                <p className="text-xs text-gray-500">{setting.description}</p>
              </div>
              <button
                onClick={() => setNotificationSettings({
                  ...notificationSettings, 
                  [setting.key]: !notificationSettings[setting.key]
                })}
                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                  notificationSettings[setting.key] ? 'bg-green-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                    notificationSettings[setting.key] ? 'translate-x-5' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          ))}
        </div>
      </div>    </div>
  );

  const createDefaultCustomer = async () => {
    try {
      setCreatingDefaultCustomer(true);
      
      // Verificar se já existe um cliente configurado
      if (pixSettings.default_customer_id) {
        const confirm = window.confirm(
          'Já existe um cliente "Consumidor Final" configurado. Deseja criar um novo cliente? ' +
          'Isso substituirá a configuração atual.'
        );
        if (!confirm) return;
      }
      
      // Verificar se as configurações PIX estão válidas
      if (!pixSettings.token) {
        toast.error('Configure o token do Asaas antes de criar o cliente');
        return;
      }
      
      const result = await dataHelpers.createDefaultCustomer(company.id);
      
      if (result.success) {
        toast.success(result.message);
        
        // Atualizar o estado local
        setPixSettings(prev => ({
          ...prev,
          default_customer_id: result.customer_id
        }));
        
        // Recarregar as configurações para garantir sincronização
        await loadSettings();
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      console.error('Erro ao criar cliente padrão:', error);
      toast.error('Erro ao criar cliente "Consumidor Final"');
    } finally {
      setCreatingDefaultCustomer(false);
    }
  };

  return (
    <>
      <div className="p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Configurações</h1>
          <p className="text-gray-600">Gerencie as configurações do seu sistema</p>
        </div>

        <div className="flex flex-col lg:flex-row gap-6">
          {/* Sidebar com abas */}
          <div className="lg:w-64">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <nav className="space-y-2">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                      activeTab === tab.id
                        ? 'bg-blue-50 text-blue-700 border border-blue-200'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                  >
                    <tab.icon className="w-4 h-4 mr-3" />
                    {tab.name}
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Conteúdo principal */}
          <div className="flex-1">
            <Card className="p-6">
              {activeTab === 'general' && renderGeneralSettings()}
              {activeTab === 'pix' && renderPixSettings()}
              {activeTab === 'whatsapp' && renderWhatsAppSettings()}
              {activeTab === 'notifications' && renderNotificationSettings()}

              {/* Botões de ação */}
              <div className="flex justify-end mt-8 pt-6 border-t border-gray-200">
                <div className="flex gap-3">
                  <Button
                    onClick={loadSettings}
                    variant="outline"
                    disabled={loading}
                  >
                    <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                    Recarregar
                  </Button>
                  <Button
                    onClick={saveSettings}
                    disabled={loading}
                  >
                    <Save className="w-4 h-4 mr-2" />
                    Salvar Configurações
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </>
  );
};

export default SettingsPage;
